import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useNavigationManager } from '../core';
import { logger } from '../core';
import { useBetaAccess } from './BetaAccessContext';
import {
  isDesktopBridgeRuntime,
  desktopBridgeLogin,
  desktopBridgeLogout,
  desktopBridgeStatus,
} from '../core/auth/desktop-auth-bridge';

const BROWSER_AUTH_EXPIRED_EVENT = 'revolt:auth-expired';

/** Mirrors backend `AccountManager` desktop entitlement denial. */
const NO_LOCAL_DESKTOP_ENTITLEMENT_CODE = 'no_local_desktop_entitlement';

/**
 * Maps API/login payloads to a failure category. Only `"auth"` means wrong credentials
 * (safe to drop a stored saved-account slot on account-switch). Desktop Local entitlement
 * and email-verification blocks must not remove saved accounts.
 */
function resolveLoginFailureType(payload, httpStatus) {
  if (payload?.code === NO_LOCAL_DESKTOP_ENTITLEMENT_CODE) {
    return 'entitlement';
  }
  if (httpStatus === 401) {
    return 'auth';
  }
  if (httpStatus === 403 && payload?.emailVerified === false) {
    return 'verification';
  }
  if (httpStatus === 403) {
    return 'server';
  }
  if (payload?.emailVerified === false) {
    return 'verification';
  }
  return 'auth';
}

// Account storage utilities
const ACCOUNT_STORAGE_KEY = 'revolt_accounts';

const getStoredAccounts = () => {
  try {
    const stored = localStorage.getItem(ACCOUNT_STORAGE_KEY);
    return stored ? JSON.parse(stored) : { accounts: [], currentAccountId: null };
  } catch (error) {
    console.error('Error reading stored accounts:', error);
    return { accounts: [], currentAccountId: null };
  }
};

const saveStoredAccounts = (accountsData) => {
  try {
    localStorage.setItem(ACCOUNT_STORAGE_KEY, JSON.stringify(accountsData));
  } catch (error) {
    console.error('Error saving stored accounts:', error);
  }
};


const updateCurrentAccount = (accountId) => {
  const storedData = getStoredAccounts();
  storedData.currentAccountId = accountId;
  saveStoredAccounts(storedData);
};

const removeStoredAccount = (accountId) => {
  const storedData = getStoredAccounts();
  storedData.accounts = storedData.accounts.filter(acc => acc.id !== accountId);
  if (storedData.currentAccountId === accountId) {
    storedData.currentAccountId = storedData.accounts.length > 0 ? storedData.accounts[0].id : null;
  }
  saveStoredAccounts(storedData);
};

const AuthContext = createContext();

// Tab-specific session management
const TAB_SESSION_KEY = 'revolt_tab_session';
const TAB_ID_KEY = 'revolt_tab_id';

const generateTabId = () => {
  return `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

const getTabId = () => {
  let tabId = sessionStorage.getItem(TAB_ID_KEY);
  if (!tabId) {
    tabId = generateTabId();
    sessionStorage.setItem(TAB_ID_KEY, tabId);
  }
  return tabId;
};

const getTabSession = () => {
  try {
    const tabId = getTabId();
    const sessionData = sessionStorage.getItem(`${TAB_SESSION_KEY}_${tabId}`);
    return sessionData ? JSON.parse(sessionData) : null;
  } catch (error) {
    console.error('Error reading tab session:', error);
    return null;
  }
};

const setTabSession = (accountId) => {
  try {
    const tabId = getTabId();
    const sessionData = { accountId, timestamp: Date.now() };
    sessionStorage.setItem(`${TAB_SESSION_KEY}_${tabId}`, JSON.stringify(sessionData));
  } catch (error) {
    console.error('Error saving tab session:', error);
  }
};

const clearTabSession = () => {
  try {
    const tabId = getTabId();
    sessionStorage.removeItem(`${TAB_SESSION_KEY}_${tabId}`);
  } catch (error) {
    console.error('Error clearing tab session:', error);
  }
};

export function AuthProvider({ children }) {
  const location = useLocation();
  const navigationManager = useNavigationManager();
  const [user, setUser] = useState(null);
  const [membershipDetails, setMembershipDetails] = useState(null);
  const [legalAcceptance, setLegalAcceptance] = useState(null);
  const [isBetaTester, setIsBetaTester] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [storedAccounts, setStoredAccounts] = useState(() => getStoredAccounts());
  const [tabSessionChecked, setTabSessionChecked] = useState(false);
  const hasBootstrappedAuthRef = useRef(false);
  const browserAuthExpiryHandledRef = useRef(false);

  // Update loading state based on auth status
  const authFullyChecked = authChecked && tabSessionChecked;

  const applyAuthPayload = useCallback((data) => {
    if (!data) return;
    if (data.user) {
      setUser(data.user);
    }
    if (Object.prototype.hasOwnProperty.call(data, 'membershipDetails')) {
      setMembershipDetails(data.membershipDetails || null);
    }
    if (Object.prototype.hasOwnProperty.call(data, 'isBetaTester')) {
      setIsBetaTester(data.isBetaTester === true);
    }
    if (Object.prototype.hasOwnProperty.call(data, 'legalAcceptance')) {
      setLegalAcceptance(data.legalAcceptance || null);
    }
  }, []);

  const checkAuthStatus = useCallback(async () => {
    try {
      if (isDesktopBridgeRuntime()) {
        const data = await desktopBridgeStatus();
        if (data.success && data.authenticated) {
          applyAuthPayload(data);
        } else {
          setUser(null);
          setMembershipDetails(null);
          setLegalAcceptance(null);
          setIsBetaTester(false);
        }
        return;
      }

      const response = await fetch('/api/account/status', {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Response is not JSON');
      }

      const data = await response.json();

      if (data.success && data.authenticated) {
        applyAuthPayload(data);
      } else {
        setUser(null);
        setMembershipDetails(null);
        setLegalAcceptance(null);
        setIsBetaTester(false);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
      setLegalAcceptance(null);
    } finally {
      setAuthChecked(true);
    }
  }, [applyAuthPayload]);

  const login = useCallback(async (email, password, rememberAccount = true) => {
    try {
      let data;
      if (isDesktopBridgeRuntime()) {
        data = await desktopBridgeLogin(email, password);
      } else {
        const response = await fetch('/api/account/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
          credentials: 'include',
        });

        // Check if response is okay before trying to parse JSON
        if (!response.ok) {
          // Network/server errors - don't treat as account invalidation
          if (response.status >= 500) {
            return { success: false, error: 'Server error', type: 'network' };
          }
          // Try to parse error response
          try {
            const errorData = await response.json();
            const message =
              errorData.message || errorData.error || 'Login failed';
            return {
              success: false,
              error: message,
              code: errorData.code || null,
              type: resolveLoginFailureType(errorData, response.status),
            };
          } catch (parseError) {
            return {
              success: false,
              error: 'Login failed',
              code: null,
              type: 'server',
            };
          }
        }

        data = await response.json();
      }

      if (data.success) {
        applyAuthPayload(data);

        // Store account credentials if login successful and rememberAccount is true
        if (rememberAccount) {
          // Check if account already exists and update it, or create new
          const storedData = getStoredAccounts();
          const existingAccountIndex = storedData.accounts.findIndex(acc => acc.email === email);

          if (existingAccountIndex !== -1) {
            // Update existing account with fresh user data
            storedData.accounts[existingAccountIndex].userData = data.user;
            storedData.accounts[existingAccountIndex].displayName = data.user.username || data.user.email || 'Account';
            storedData.currentAccountId = storedData.accounts[existingAccountIndex].id;
          } else {
            // Create new account
            const accountId = `acc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            const newAccount = {
              id: accountId,
              email,
              password, // Store password securely (in production, consider encryption)
              displayName: data.user.username || data.user.email || 'Account',
              userData: data.user,
              lastLogin: new Date().toISOString()
            };
            storedData.accounts.push(newAccount);
            storedData.currentAccountId = accountId;
          }

          saveStoredAccounts(storedData);
          setStoredAccounts(storedData); // Refresh stored accounts state
          broadcastAccountUpdate(); // Notify other tabs

          // Store this account as the session for this tab
          const currentAccountId = storedData.currentAccountId;
          setTabSession(currentAccountId);
          logger.log(
            { tags: 'auth|tab-session', color1: 'green' },
            `Set tab session for account: ${storedData.accounts.find(acc => acc.id === currentAccountId)?.displayName}`
          );
        }

      return { success: true };
      } else {
        const message =
          data.message || data.error || 'Login failed';
        return {
          success: false,
          error: message,
          code: data.code || null,
          type: resolveLoginFailureType(
            data,
            typeof data.httpStatus === 'number' ? data.httpStatus : null
          ),
        };
      }
    } catch (error) {
      // Network errors (fetch failed) - don't remove accounts
      logger.warn({ tags: 'auth|login', color1: 'orange' }, 'Login network error:', error);
      return {
        success: false,
        error: 'Network error',
        code: null,
        type: 'network',
      };
    }
  }, [applyAuthPayload]);

  // Clean up stored accounts that no longer exist (called when we detect account issues)
  const cleanupInvalidAccounts = useCallback(() => {
    if (!user) return;

    const storedData = getStoredAccounts();
    const currentEmail = user.email;

    // Remove accounts that don't match the current user and aren't the current account
    storedData.accounts = storedData.accounts.filter(account => {
      return account.email === currentEmail || account.id === storedData.currentAccountId;
    });

    // If current account was removed and we have accounts, set first one as current
    if (!storedData.accounts.find(acc => acc.id === storedData.currentAccountId) && storedData.accounts.length > 0) {
      storedData.currentAccountId = storedData.accounts[0].id;
    }

    saveStoredAccounts(storedData);
    setStoredAccounts(storedData);
  }, [user]);

  const checkTabSessionAndAuth = useCallback(async () => {
    // Electron desktop embeds the site in an iframe (not true browser tabs).
    // In this runtime, skip tab-session auto-login and rely on cookie-backed auth status.
    if (typeof window !== 'undefined' && window.self !== window.top) {
      logger.log(
        { tags: 'auth|init|embedded', color1: 'blue' },
        'Embedded runtime detected, skipping tab-session bootstrap and checking auth status directly'
      );
      await checkAuthStatus();
      setTabSessionChecked(true);
      return;
    }

    const tabId = getTabId();
    logger.log(
      { tags: 'auth|init', color1: 'blue' },
      `Initializing auth for tab: ${tabId}`
    );

    // First check if this tab has a specific account it should be logged into
    const tabSession = getTabSession();
    if (tabSession && tabSession.accountId) {
      const account = storedAccounts.accounts.find(acc => acc.id === tabSession.accountId);
      if (account) {
        // This tab should be logged into a specific account, try to login automatically
        logger.log(
          { tags: 'auth|tab-session', color1: 'cyan' },
          `Tab ${tabId} has stored session for account: ${account.displayName} (${account.email})`
        );

        try {
          const loginResult = await login(account.email, account.password, false);
          if (loginResult.success) {
            logger.log(
              { tags: 'auth|tab-session', color1: 'green' },
              `Tab ${tabId} auto-logged into tab session account: ${account.displayName}`
            );
            // Hydrate full user state (including supporter flag) from /account/status
            await checkAuthStatus();
            setTabSessionChecked(true);
            return;
          } else {
            logger.warn(
              { tags: 'auth|tab-session', color1: 'yellow' },
              `Tab ${tabId} failed to auto-login to tab session account: ${loginResult.error}`
            );
            // Clear the invalid tab session
            clearTabSession();
          }
        } catch (error) {
          logger.error(
            { tags: 'auth|tab-session', color1: 'red' },
            `Tab ${tabId} failed to auto-login to tab session account:`,
            error
          );
          // Clear the invalid tab session
          clearTabSession();
        }
      } else {
        logger.warn(
          { tags: 'auth|tab-session', color1: 'yellow' },
          `Tab ${tabId} has session for account ${tabSession.accountId} but account not found in stored accounts`
        );
        clearTabSession();
      }
    } else {
      logger.log(
        { tags: 'auth|tab-session', color1: 'blue' },
        `Tab ${tabId} has no stored session`
      );
    }

    // No tab session or auto-login failed, check normal authentication
    logger.log(
      { tags: 'auth|normal', color1: 'blue' },
      `Tab ${tabId} falling back to normal auth check`
    );
    await checkAuthStatus();
    setTabSessionChecked(true);
  }, [storedAccounts, login, checkAuthStatus]);

  // Check authentication status on app load
  useEffect(() => {
    if (hasBootstrappedAuthRef.current) {
      return;
    }
    hasBootstrappedAuthRef.current = true;
    checkTabSessionAndAuth();
  }, [checkTabSessionAndAuth]);

  useEffect(() => {
    if (isDesktopBridgeRuntime()) {
      return undefined;
    }

    const handleBrowserAuthExpired = async (event) => {
      if (browserAuthExpiryHandledRef.current) {
        return;
      }

      browserAuthExpiryHandledRef.current = true;
      const currentUrl = `${location.pathname}${location.search}`;

      logger.warn(
        { tags: 'auth|session-expired', color1: 'yellow' },
        'Browser session expired during an API request, redirecting to auth',
        {
          currentUrl,
          requestPath: event?.detail?.requestPath || null,
        }
      );

      setUser(null);
      setMembershipDetails(null);
      setLegalAcceptance(null);
      setIsBetaTester(false);
      clearTabSession();
      setAuthChecked(true);
      setTabSessionChecked(true);

      if (!location.pathname.startsWith('/auth')) {
        await navigationManager.requireAuth(currentUrl, {
          action: 'resumeAfterSessionExpiry',
          source: 'browserSessionExpiry',
          requestPath: event?.detail?.requestPath || null,
          timestamp: Date.now(),
        });
      }

      window.setTimeout(() => {
        browserAuthExpiryHandledRef.current = false;
      }, 500);
    };

    window.addEventListener(BROWSER_AUTH_EXPIRED_EVENT, handleBrowserAuthExpired);
    return () => {
      window.removeEventListener(BROWSER_AUTH_EXPIRED_EVENT, handleBrowserAuthExpired);
    };
  }, [location.pathname, location.search, navigationManager]);

  // Listen for localStorage changes (cross-tab synchronization)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === ACCOUNT_STORAGE_KEY) {
        setStoredAccounts(getStoredAccounts());
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // BroadcastChannel for cross-tab account sync
  useEffect(() => {
    if (typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel('revolt_accounts');

      channel.onmessage = (event) => {
        if (event.data.type === 'accounts_updated') {
          setStoredAccounts(getStoredAccounts());
        }
      };

      return () => {
        channel.close();
      };
    }
  }, []);

  const refreshUser = async () => {
    await checkAuthStatus();
  };

  const refreshCredits = async () => {
    try {
      const response = await fetch('/api/account/credits', {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Response is not JSON');
      }

      const data = await response.json();

      if (data.success && data.credits) {
        // Update only the credits in membershipDetails without triggering full auth refresh
        setMembershipDetails(prevDetails => {
          if (!prevDetails) return prevDetails;
          return {
            ...prevDetails,
            currentCredits: data.credits.currentCredits,
            monthlyCredits: data.credits.monthlyCredits,
          };
        });
      }
    } catch (error) {
      console.error('Credit refresh failed:', error);
      // Don't throw - credit refresh failure shouldn't break the app
    }
  };

  const acceptLegalDocuments = async (options = {}) => {
    const response = await fetch('/api/account/legal/accept', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        slugs: Array.isArray(options.slugs) ? options.slugs : [],
        acceptAllPending: options.acceptAllPending === true,
      }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data?.success) {
      throw new Error(data?.error || 'Failed to record legal acceptance');
    }
    if (data.legalAcceptance) {
      setLegalAcceptance(data.legalAcceptance);
    } else {
      await checkAuthStatus();
    }
    return data.legalAcceptance;
  };

  const register = async (email, username, password, legalAcceptanceBody) => {
    const response = await fetch('/api/account/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, username, password, legalAcceptance: legalAcceptanceBody }),
      credentials: 'include',
    });

    const data = await response.json();

    if (data.success) {
      // For now, automatically log them in after registration
      return await login(email, password);
    } else {
      // Handle validation errors
      if (data.errors && Array.isArray(data.errors)) {
        return { 
          success: false, 
          error: data.errors.map(e => e.msg || e.message).join(', '),
          errors: data.errors
        };
      }
      return { success: false, error: data.error || 'Registration failed' };
    }
  };

  const logout = async () => {
    try {
      if (isDesktopBridgeRuntime()) {
        await desktopBridgeLogout();
      } else {
        await fetch('/api/account/logout', {
          method: 'POST',
          credentials: 'include',
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setMembershipDetails(null);
      setLegalAcceptance(null);
      setIsBetaTester(false);
      // Clear this tab's session
      clearTabSession();
    }
  };

  // Account switching functions
  const switchToAccount = async (accountId) => {
    const account = storedAccounts.accounts.find(acc => acc.id === accountId);
    if (!account) {
      return { success: false, error: 'Account not found' };
    }

    // Capture the previous account before logging out so we can restore on non-auth failure
    // (entitlement / verification / network) and avoid stranding the user signed-out.
    const previousAccountId = storedAccounts.currentAccountId;
    const previousAccount =
      previousAccountId && previousAccountId !== accountId
        ? storedAccounts.accounts.find((a) => a.id === previousAccountId)
        : null;

    // First logout current user
    await logout();

    // Then login with the selected account
    const loginResult = await login(account.email, account.password, false); // Don't store again

    if (loginResult.success) {
      updateCurrentAccount(accountId);
      setStoredAccounts(getStoredAccounts()); // Refresh state
      broadcastAccountUpdate(); // Notify other tabs

      // Set this account as the session for this tab
      setTabSession(accountId);
      logger.log(
        { tags: 'auth|tab-session', color1: 'green' },
        `Switched tab session to account: ${account.displayName}`
      );
      return loginResult;
    }

    // Login failed - check the type of failure (only wrong-password / invalid credentials)
    if (loginResult.type === 'auth') {
      // Credentials rejected — safe to remove saved slot for this stored account
      logger.warn(
        { tags: 'auth|account-cleanup', color1: 'yellow' },
        `Removing invalid stored account: ${account.displayName} (${account.email}) - auth failed`
      );
      removeStoredAccount(accountId);
      setStoredAccounts(getStoredAccounts()); // Refresh state
      broadcastAccountUpdate(); // Notify other tabs
    } else {
      // Non-auth failure (entitlement / verification / network) — keep both accounts and
      // restore the previous session so the user is not stranded logged-out.
      logger.warn(
        { tags: 'auth|switch-account', color1: 'orange' },
        `Account switch failed due to ${loginResult.type} error: ${account.displayName} (${account.email}) - keeping in stored accounts`
      );
      if (previousAccount) {
        const restoreResult = await login(
          previousAccount.email,
          previousAccount.password,
          false
        );
        if (restoreResult.success) {
          updateCurrentAccount(previousAccountId);
          setStoredAccounts(getStoredAccounts());
          setTabSession(previousAccountId);
          logger.log(
            { tags: 'auth|switch-account', color1: 'green' },
            `Restored previous account session after failed switch: ${previousAccount.displayName}`
          );
        } else {
          logger.warn(
            { tags: 'auth|switch-account', color1: 'orange' },
            `Failed to restore previous account after failed switch: ${previousAccount.displayName} (${restoreResult.type})`
          );
        }
      }
    }

    return {
      ...loginResult,
      attemptedEmail: account.email,
      attemptedDisplayName: account.displayName,
    };
  };

  const removeAccount = (accountId) => {
    removeStoredAccount(accountId);
    setStoredAccounts(getStoredAccounts()); // Refresh state
    broadcastAccountUpdate(); // Notify other tabs
  };

  const getCurrentAccount = () => {
    if (!user || !storedAccounts.currentAccountId) return null;
    return storedAccounts.accounts.find(acc => acc.id === storedAccounts.currentAccountId);
  };

  const refreshStoredAccounts = () => {
    setStoredAccounts(getStoredAccounts());
  };

  // Update stored account data for all accounts belonging to the current user (called after profile updates)
  const updateStoredAccountsForUser = useCallback((updatedUserData) => {
    if (!user) return;

    const storedData = getStoredAccounts();
    const currentUserEmail = user.email;

    // Update all accounts that belong to the current user
    let updated = false;
    storedData.accounts.forEach((account, index) => {
      if (account.email === currentUserEmail) {
        // Update the userData for this account
        storedData.accounts[index].userData = {
          ...storedData.accounts[index].userData,
          ...updatedUserData
        };
        // Also update displayName if username changed
        if (updatedUserData.username) {
          storedData.accounts[index].displayName = updatedUserData.username;
        }
        updated = true;
      }
    });

    if (updated) {
      saveStoredAccounts(storedData);
      setStoredAccounts(storedData);
      broadcastAccountUpdate();
    }
  }, [user]);

  // Broadcast account updates to other tabs
  const broadcastAccountUpdate = () => {
    if (typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel('revolt_accounts');
      channel.postMessage({ type: 'accounts_updated' });
      setTimeout(() => channel.close(), 100); // Close after sending
    }
  };

  const legalAcceptanceComplete =
    !user || (legalAcceptance?.isComplete === true);

  const value = {
    user,
    membershipDetails,
    legalAcceptance,
    legalAcceptanceComplete,
    isBetaTester,
    isSupporter: !!user?.isSupporter,
    loading: !authFullyChecked,
    authChecked: authFullyChecked,
    login,
    register,
    logout,
    checkAuthStatus,
    acceptLegalDocuments,
    refreshUser,
    refreshCredits,
    isAuthenticated: !!user && authFullyChecked,
    // Account management
    storedAccounts: storedAccounts.accounts,
    switchToAccount,
    removeAccount,
    getCurrentAccount,
    refreshStoredAccounts,
    updateStoredAccountsForUser,
    cleanupInvalidAccounts,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Hook for requiring authentication with custom payload support
 * @param {Object} options - Configuration options
 * @param {Object} options.payload - Data to preserve across login
 * @param {boolean} options.requireAuth - Whether to enforce authentication check
 * @returns {Object} Auth state and payload data
 */
// FRONTEND-ENFORCED BETA TESTING
// Beta testing requirements are enforced both on frontend (page access) and backend (API access)
// Frontend prevents access to pages, backend protects API endpoints
export function useAuthRequired(options = {}) {
  const { payload = {}, requireAuth = true, requireBetaTester = false, requireSupporter = false, requireAdmin = false } = options;
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const navigationManager = useNavigationManager();
  const betaAccess = useBetaAccess();

  // Check for return payload data (after login redirect)
  const returnData = navigationManager.getAuthReturnData();
  const hasReturnPayload = returnData && returnData.payload;

  // Check both authentication and various role requirements when required
  const isAuthorized = auth.isAuthenticated;
  const isBetaAuthorized = requireBetaTester ? (auth.isAuthenticated && auth.isBetaTester) : auth.isAuthenticated;
  const isSupporterAuthorized = requireSupporter ? (auth.isAuthenticated && auth.isSupporter) : auth.isAuthenticated;
  const isAdminAuthorized = requireAdmin ? (auth.isAuthenticated && auth.user?.role === 'admin') : auth.isAuthenticated;

  useEffect(() => {
    // Handle authentication requirement
    if (requireAuth && !isAuthorized && !auth.loading) {
      const currentUrl = location.pathname + location.search;
      navigationManager.requireAuth(currentUrl, payload);
    }

    // Handle beta tester requirement - show beta access modal and redirect
    if (requireBetaTester && isAuthorized && !auth.isBetaTester && !auth.loading) {
      logger.log(
        { tags: 'auth|beta-required', color1: 'yellow' },
        'Beta tester access required, showing modal and redirecting to main'
      );
      betaAccess.showBetaAccessModal();
      // Give user time to see the modal before redirecting
      const timeoutId = setTimeout(() => {
        // Only redirect if the user is still not a beta tester at the time the timer fires
        if (!auth.isBetaTester) {
          navigate('/main');
        }
      }, 2500);

      return () => clearTimeout(timeoutId);
    }

    // Handle supporter requirement (frontend gating only; backend must protect APIs too)
    if (requireSupporter && isAuthorized && !auth.isSupporter && !auth.loading) {
      logger.log(
        { tags: 'auth|supporter-required', color1: 'yellow' },
        'Supporter access required, redirecting to main'
      );
      navigate('/main');
    }

    // Handle admin requirement (frontend gating only; backend must protect APIs too)
    if (requireAdmin && isAuthorized && auth.user?.role !== 'admin' && !auth.loading) {
      logger.log(
        { tags: 'auth|admin-required', color1: 'red' },
        'Admin access required, redirecting to main'
      );
      navigate('/main');
    }
  }, [requireAuth, requireBetaTester, requireSupporter, requireAdmin, isAuthorized, auth.isBetaTester, auth.isSupporter, auth.user?.role, auth.loading, location, navigationManager, payload, betaAccess, navigate]);

  return {
    ...auth,
    returnPayload: hasReturnPayload ? returnData.payload : null,
    clearReturnPayload: () => navigationManager.clearAuthReturnData(),
    // Add role info to the returned object
    isBetaTester: auth.isBetaTester,
    isAdmin: auth.user?.role === 'admin',
    isAuthorized: requireAdmin ? isAdminAuthorized : requireBetaTester ? isBetaAuthorized : requireSupporter ? isSupporterAuthorized : isAuthorized
  };
}

/**
 * Simple hook for requiring authentication without custom payload
 * @returns {Object} Auth state
 */
// Requires authentication - backend enforces beta testing where needed
export function useRequireAuth() {
  return useAuthRequired({ payload: {}, requireAuth: true, requireBetaTester: false });
}
